import CanvasDragTarget, { DRAG_TARGET_TYPE, measureTransformedBounds } from './CanvasDragTarget';

// A line of text on the canvas: the artist name, the track name or any line the user added. Dragged
// only, never resized, so its size stays with the Font Size control alone.
export default class TextElementDragTarget extends CanvasDragTarget {

    constructor(textElement, index, onCommit) {
        super(`text:${index}`, DRAG_TARGET_TYPE.TEXT);

        this.textElement = textElement;
        this.index = index;
        this.onCommit = onCommit;
    }

    // The letters themselves. The glow and the shadow are left out on purpose: they spread around the
    // text by however much their sliders say, and an outline that grew with them stopped reading as
    // the outline of the text. An emptied line never gets this far, because the collector skips it.
    getBounds() {
        const { elementContainer, text } = this.textElement;

        if (!elementContainer.visible)
            return null;

        return measureTransformedBounds(text, measureLetterRectangle(text));
    }

    getPosition() {
        return { ...this.textElement.config.position };
    }

    // A line anchored left starts at its position and runs right from it, and one anchored right ends
    // there, so its box can sit up to its whole width away from the position that is stored.
    measureOffsetFromPosition(position) {
        const box = this.measureBox();

        if (!box)
            return { x: 0, y: 0 };

        return { x: box.centerX - position.x, y: box.centerY - position.y };
    }

    moveTo(position) {
        this.textElement.moveTo(position);
    }

    // Addressed by index, which is what the Text panel and the store both use. Nothing can reorder the
    // lines while a pointer is down on the canvas.
    commit(position) {
        this.onCommit(this.index, { position });
    }
}

// Where the letters of a PIXI.Text sit, in its own space.
//
// PIXI draws text onto a canvas grown by the style's padding on every side, and TextElement pads it
// generously (a third of the font size, plus room for the shadow and the glow) so nothing is ever
// clipped. The bounds PIXI reports cover that whole canvas, which is what put the outline so far out
// from the text. The texture's orig size is the canvas without that padding, and it is also the size
// the anchor is applied to, so the letters start exactly at the anchored corner of it.
//
// What is left over is the drop shadow, which PIXI measures into the right and bottom of that size. It
// is taken back off those two sides, the same correction PIXI itself makes when it fits a gradient fill
// to the letters.
//
// Shared with the lyrics target, which outlines the same kind of text element.
export function measureLetterRectangle(text) {
    // Reading PIXI's own bounds would redraw a text that changed since the last frame before measuring
    // it, and so must this, or a line typed a moment ago would be outlined at its old length.
    text.updateText(true);

    const { orig } = text.texture;
    const shadowDistance = text.style.dropShadow ? text.style.dropShadowDistance : 0;

    return {
        x: -text.anchor.x * orig.width,
        y: -text.anchor.y * orig.height,
        width: orig.width - shadowDistance,
        height: orig.height - shadowDistance
    };
}
